<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import { CustomFieldData, DataType } from "./types";
import { useUploadStore } from "~/stores/upload.store";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { SysFieldName } from "~/entities/contacts";

const { allColumns } = storeToRefs(useUploadStore());
const { t } = useI18n();

const options = [
  {
    value: "string",
    text: "Текст",
    description:
      "Строки вида: «test@example.com», «Алексей Иванович» и т.п. Доступен поиск по тексту.",
  },
  {
    value: "number",
    text: "Число",
    description:
      "Числовые значения вида: «20», «30000»,«-40», «1», «0». Доступен поиск по значению и диапазону.",
  },
  {
    value: "date",
    text: "Дата",
    description:
      "Дата вида: «31.12.2022, 10:21:18 UTC», «31/12/2022, 10:21:18 UTC», «31-12-2022, 10:21:18 UTC». Доступен поиск по дате и периоду.",
  },
];
const RESERVED_NAME = ref<string[]>([]);

const visibleModel = ref<boolean>(false);
const name = ref<string>("");
const selectedType = ref<DataType>("string");

const resolvePromise = ref<(value: CustomFieldData) => void>();
const rejectPromise = ref<(value: unknown) => void>();

const reset = () => {
  name.value = "";
  selectedType.value = "string";
};

const hide = () => {
  visibleModel.value = false;
};

const onClose = () => {
  hide();
  rejectPromise.value!(null);
};

const show = (): Promise<CustomFieldData> => {
  visibleModel.value = true;
  return new Promise((resolve, reject) => {
    resolvePromise.value = resolve;
    rejectPromise.value = reject;
  });
};

defineExpose({
  show,
});

const error = computed(() => {
  if (
    allColumns.value.some(
      (column) =>
        column.name?.toLowerCase() === name.value.toLowerCase().trim() &&
        column.dataType === selectedType.value,
    )
  ) {
    return "Колонка с таким названием и типом уже существует";
  } else if (
    RESERVED_NAME.value.some(
      (reserved) => reserved.toLowerCase() === name.value.toLowerCase().trim(),
    )
  ) {
    return "Колонка с таким названием уже существует";
  } else {
    return false;
  }
});

const isDisabled = computed(() => name.value.length === 0 || !!error.value);

const onClick = () => {
  resolvePromise.value!({
    name: name.value.trim(),
    dataType: selectedType.value,
  });
  visibleModel.value = false;
  reset();
};

watch(visibleModel, (val) => {
  if (val) return;
  reset();
});

onMounted(() => {
  RESERVED_NAME.value = [
    t(`customers.header.${SysFieldName.Phone}`),
    t(`customers.header.${SysFieldName.Name}`),
    t(`customers.header.${SysFieldName.Birthday}`),
    t(`customers.header.${SysFieldName.Tg_Nick}`),
    t(`customers.header.${SysFieldName.Status}`),
    t(`customers.header.${SysFieldName.UploadMethod}`),
    t(`customers.header.${SysFieldName.UploadDate}`),
    t(`customers.header.${SysFieldName.UpdateDate}`),
    t(`customers.header.${SysFieldName.LastSend}`),
    t('customers.header.tag'),
  ];
});
</script>

<template>
  <PDialog
    v-model="visibleModel"
    width="x-small"
    :close-on-click-outside="true"
  >
    <template #header>
      <header>
        <p>Новая колонка</p>
        <IconCrossMd class="icon" @click="onClose" />
      </header>
    </template>
    <div class="main upload-add-field">
      <PInput
        v-model="name"
        :label="'Название'"
        :placeholder="''"
        :error="error"
        :disabled="false"
        :size="'large'"
        :text-size="'medium'"
        :hint="''"
        :type="'text'"
        :autocomplete="'off'"
        :autofocus="true"
        :readonly="false"
        :lost-focus-on-enter="false"
        :max-length="30"
        :input-mode="'text'"
        :border="'default'"
        :mask="undefined"
        :ignore-click="false"
      >
      </PInput>
      <PSelect
        v-model="selectedType"
        :label="'Тип данных'"
        :placeholder="''"
        :options="options"
        :hint="''"
        :disabled="false"
        :max-height="297"
        :allow-custom-value="false"
        :hide-empty-menu="false"
        :autocomplete="'off'"
        :border="'default'"
        :size="'large'"
        :input-mode="'text'"
        :mask="''"
        :selected-on-top="true"
        :readonly="false"
      >
        <template #default="{ item }" class="select">
          <div class="select-option">
            <span class="select-option-title">{{ item.text }}</span>
            <span class="select-option-description">{{
              item.description
            }}</span>
          </div>
        </template>
      </PSelect>
      <PButton
        :variant="'default'"
        :size="'small'"
        :color="'primary'"
        :disabled="isDisabled"
        :nuxt="false"
        :type="'button'"
        :block="true"
        @click="onClick"
        >Добавить</PButton
      >
    </div>
    <template #footer>
      <footer></footer>
    </template>
  </PDialog>
</template>

<style scoped>
header {
  display: flex;
  position: relative;
  box-sizing: border-box;
  padding: 12px 16px;
  box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.08) inset;
}

header p {
  width: 100%;
  text-align: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
}

header .icon {
  position: absolute;
  right: 16px;
  cursor: pointer;
}

.main {
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.select-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.select-option-description {
  font-weight: 400;
  font-size: 12px;
  color: var(--common-asphalt);
  line-height: 16px;
}

.select-option-title {
  font-size: 14px;
}
</style>

<style>
.main.upload-add-field .p-options-option.selected.active {
  position: relative;
  opacity: 1;
}

.main.upload-add-field .p-options-option.selected.active svg {
  position: absolute;
  top: 2px;
  right: 0px;
}
</style>
